/**
 * useStaff — staff + group data hook backed by @v0y4ge/api-client.
 * No seed data in the UI. All data comes from the contract layer.
 *
 * Restored 2026-07-03 (Claude Code push-back, drop blocker 1a): the previous
 * revision narrowed this hook to list-only local types and dropped the
 * canonical contract imports + mutation calls. The create/update/delete/
 * duplicate operations below all exist in @v0y4ge/api-contracts + the real
 * backend today — this hook now calls them for real, with an optimistic
 * local fallback ONLY for the preview harness (whose fixture stub doesn't
 * implement staff mutations yet — see index.html STUBS).
 */
import { useState, useEffect } from "react";
import { T } from "@v0y4ge/jrni-ui";
import type { CanonicalStaffMember, CanonicalStaffGroup, StaffLocationAssignment } from "@v0y4ge/api-contracts";
import { logUpsert, logDelete } from "../../lib/audit";
import {
  listStaff, listStaffGroups, setStaffPhoto, deleteStaffPhoto,
  createStaff, updateStaff, updateStaffStatus, deleteStaff, duplicateStaff, setStaffLocationRoles,
  createStaffGroup, updateStaffGroup, deleteStaffGroup, duplicateStaffGroup,
} from "@v0y4ge/api-client";
import { can, onSessionEnd, onTenantChange, onTenantSwitching } from "../../shell/access";

// UI-facing shapes are the canonical contract types (byte-for-byte — no local
// re-declaration to drift out of sync). `updated`/`services` below are the
// adapter's derived convenience fields the screens already read.
export type StaffMember = CanonicalStaffMember & { services?: number; resourceIds?: string[];
  /** The sign-in account this member is linked to, when the read carries it (CC 0374). Its address lives on
   *  the account (listTeamMembers), not on this row; null/absent = not linked. */
  userId?: string | null };
export type StaffGroup = CanonicalStaffGroup;

function toMember(m: Record<string, any>): StaffMember {
  const first = m.firstName ?? m.first_name ?? "";
  const last = m.lastName ?? m.last_name ?? "";
  const photoUrl = m.photoUrl ?? m.photo_url ?? m.imageUrl ?? m.image_url ?? m.photo ?? undefined;
  return {
    id: m.id, staffId: m.staffId ?? m.staff_id ?? m.id,
    firstName: first, lastName: last,
    name: m.name ?? `${first} ${last}`.trim(),
    email: m.email ?? "", phone: m.phone ?? undefined, title: m.title ?? m.role ?? "",
    role: m.role ?? m.title ?? null,
    bio: m.bio ?? "",
    photo: m.photo ?? photoUrl ?? undefined,
    photoUrl: photoUrl ?? null,
    status: m.status ?? "active", signsIn: m.signsIn ?? m.signs_in ?? true,
    channels: m.channels ?? [], groups: m.groups ?? [],
    locations: m.locations ?? (m.location ?? m.locationName ? [m.location ?? m.locationName] : undefined),
    serviceGrants: m.serviceGrants ?? m.service_grants ?? [],
    serviceExclusions: m.serviceExclusions ?? m.service_exclusions ?? [],
    resourceIds: m.resourceIds ?? m.resource_ids ?? [],
    skills: m.skills ?? [], languages: m.languages ?? [],
    department: m.department ?? null, category: m.category ?? null,
    tags: m.tags ?? [],
    concurrency: m.concurrency ?? null, dailyCap: m.dailyCap ?? m.daily_cap ?? null,
    timeOff: m.timeOff ?? m.time_off ?? [],
    /* Default display zone (2026-08-07, Pat) — IANA id or null; null means
       "fall back to the workspace display zone", never "browser zone". */
    timezone: m.timezone ?? m.time_zone ?? null,
    /* Added by the Microsoft directory staff sync (CC 0168); absent reads as false. */
    directorySynced: (m.directorySynced ?? m.directory_synced) === true,
    slug: m.slug ?? undefined, linkEnabled: m.linkEnabled ?? m.link_enabled ?? false,
    userId: (m.userId ?? m.user_id ?? null) || null,
    color: m.color ?? undefined,
    createdAt: m.createdAt ?? m.created_at ?? undefined,
    updatedAt: m.updatedAt ?? m.updated_at ?? undefined,
    services: m.services ?? undefined,
  } satisfies StaffMember;
}
function toGroup(g: Record<string, any>): StaffGroup {
  return {
    id: g.id, name: g.name ?? "", description: g.description ?? "", hue: g.hue ?? g.color ?? T.p1,
    status: g.status ?? "published", services: g.services ?? [], memberCount: g.memberCount ?? 0,
    updatedAt: g.updatedAt ?? g.updated_at ?? undefined,
  } as StaffGroup;
}
/**
 * Deactivating clears the member's working hours on the server but keeps the
 * locations they are assigned to (2026-10-08). The cache mirrors that: each
 * assignment keeps its location and role and loses its `schedules`. An
 * assignment with no `schedules` key leaves the working rule as it is on a later
 * write (api-contracts StaffLocationAssignment), so an unrelated edit in the
 * same session cannot send the cleared hours back.
 */
function withoutSchedules(locations: StaffMember["locations"]): StaffMember["locations"] {
  if (!Array.isArray(locations)) return locations;
  return locations.map((a) => {
    if (!a || typeof a !== "object") return a;
    const kept = { ...a };
    delete kept.schedules;
    return kept;
  });
}

/**
 * Shared cache — hydrated from the typed client; mutations call the real
 * create/update/delete/duplicate operations and reconcile the response into
 * the cache (so the list, groups, and editor screens stay consistent within
 * a session; full server-side persistence is what these calls now provide).
 */
interface StaffState { members: StaffMember[]; groups: StaffGroup[]; }
let _cache: StaffState | null = null;
let _loading = false;
/** Set only when a list read FAILED — never for an empty roster (2026-07-31 rule;
 *  screens pass this to <LoadState> so an outage cannot look like "no staff"). */
let _error: string | null = null;
/** Bumped the instant a tenant switch starts: a read begun under the outgoing
 *  tenant stops paging and its result is dropped instead of landing in the
 *  incoming tenant's cache. */
let _gen = 0;
const _listeners = new Set<() => void>();
const _notify = () => _listeners.forEach((fn) => fn());
// Team roster leak fix (docket 2026-07-26): purge + refetch on tenant switch.
// Cross-tenant leak fix (docket 2026-07-27, item 1): purge SYNCHRONOUSLY the
// instant a switch starts (`_loading = true` also BLOCKS an incidental
// hydrate() call during the pending window); (re)hydrate only once the
// tenant is CONFIRMED — a fetch fired any earlier would still run under the
// outgoing tenant's session against a real backend.
onTenantSwitching(() => { _gen += 1; _cache = null; _loading = true; _notify(); });
onTenantChange(() => { _loading = false; if (can("team.read")) hydrate(); }); // admin/staff — gated (docket 2026-09-17); Staff route is team.read
/* Signing out drops the staff directory (CC, 2026-10-10): every member's name, email and phone. A read
   still in flight is dropped rather than filling it for the next person, and the next screen that
   needs the directory reads it again. Not the switch purge above, which holds the store until a
   workspace switch confirms; a sign-in never sends one. */
onSessionEnd(() => { _gen += 1; _cache = null; _loading = false; _error = null; _notify(); });
function _set(patch: Partial<StaffState>) {
  _cache = { members: _cache?.members ?? [], groups: _cache?.groups ?? [], ...patch };
  _notify();
}
/* FENCED WRITE (CC 0470). A store method captures _gen BEFORE its request and writes the cache only if no switch or
   sign-out happened meanwhile. Otherwise a late answer would turn a purged cache (null) into a non-null roster: an
   empty one after sign-out (the next sign-in then skips hydration and shows nobody), or one carrying the old
   workspace's write onto the next workspace's same-id member. The method still returns its value. */
function _setAt(gen: number, patch: Partial<StaffState>): boolean {
  if (gen !== _gen) return false;
  _set(patch);
  return true;
}
/* THE PHOTO BELONGS TO THE PHOTO METHODS (CC 0476). A record write (update, status, location roles, default role)
   answers the member as the server held it when that write ran, which may be BEFORE a photo upload or removal that
   finished first. So a record response is merged over the CACHED member as it is at write time, keeping the cached
   photo and photoUrl, and never replaces it from a snapshot taken before the await. Only setPhoto / removePhoto
   write those two fields. A brand-new member (no cached row) is taken as it is. Answers the member as written. */
function _mergeRecordAt(gen: number, id: string, saved: StaffMember): StaffMember {
  const cur = (_cache?.members ?? []).find((x) => x.id === id);
  const merged = cur ? { ...saved, photo: cur.photo, photoUrl: cur.photoUrl } : saved;
  _setAt(gen, { members: cur ? (_cache?.members ?? []).map((x) => (x.id === id ? merged : x)) : [merged, ...(_cache?.members ?? [])] });
  return merged;
}
/** Tests only: the raw cache, to tell "purged" (null) from "an empty roster". */
export const __staffCacheForTests = (): StaffState | null => _cache;
/*
 * The WHOLE roster, every page (2026-10-08, Studio bot blocker). The staff
 * route caps `limit` at 100 and sorts every status together before it pages,
 * and the Staff list filters and counts the cached members locally. So one
 * read could hold 100 deactivated people while an active one sat on page 2,
 * and the screen said "No current staff". Hydration now reads page 1..N and
 * reports done only after the last page. A failed page, or paging metadata
 * that does not add up, rejects the whole read: a partial roster is never
 * shown as the complete one.
 */
const STAFF_PAGE_LIMIT = 100;
/** Hard ceiling on pages read, so metadata that never says "no more" cannot loop. */
const STAFF_MAX_PAGES = 500;

async function listAllStaff(isCurrent: () => boolean): Promise<Record<string, any>[]> {
  const rows: Record<string, any>[] = [];
  const seen = new Set<string>();
  let total: number | null = null;
  let pages: number | null = null;
  for (let page = 1; page <= STAFF_MAX_PAGES; page += 1) {
    if (!isCurrent()) throw new Error("staff read superseded by a tenant switch");
    const r = (await listStaff({ page: String(page), limit: String(STAFF_PAGE_LIMIT) })) as unknown as Record<string, any>;
    const data = r?.data;
    if (!Array.isArray(data)) throw new Error(`staff page ${page}: response has no data array`);
    if (r.page !== page) throw new Error(`staff page ${page}: answered as page ${String(r.page)}`);
    if (typeof r.hasMore !== "boolean") throw new Error(`staff page ${page}: hasMore is missing`);
    if (!Number.isInteger(r.total) || r.total < 0) throw new Error(`staff page ${page}: total is missing`);
    const limit = Number.isInteger(r.limit) && r.limit > 0 ? (r.limit as number) : STAFF_PAGE_LIMIT;
    const derived = Math.max(1, Math.ceil((r.total as number) / limit));
    /* StaffListResponse requires total_pages and the real route serves it; the
       design-time serving engine omits it, so there it is derived from
       total/limit. Where it is served it must agree with total/limit. */
    const pageCount = r.total_pages === undefined ? derived : r.total_pages;
    if (pageCount !== derived) throw new Error(`staff page ${page}: total_pages ${String(r.total_pages)} disagrees with total ${r.total} / limit ${limit}`);
    if (total === null) { total = r.total as number; pages = pageCount as number; }
    else if (r.total !== total || pageCount !== pages) throw new Error(`staff page ${page}: roster changed while loading (total ${total} -> ${r.total})`);
    if (r.hasMore !== page < (pageCount as number)) throw new Error(`staff page ${page}: hasMore ${r.hasMore} disagrees with total_pages ${pageCount}`);
    if (data.length > limit || (r.hasMore && data.length !== limit)) throw new Error(`staff page ${page}: ${data.length} rows for a page of ${limit}`);
    for (const m of data) {
      const id = m?.id;
      if (typeof id !== "string" || !id) throw new Error(`staff page ${page}: a member has no id`);
      if (seen.has(id)) throw new Error(`staff page ${page}: member ${id} appeared twice (roster changed while loading)`);
      seen.add(id);
      rows.push(m);
    }
    if (!r.hasMore) {
      if (rows.length !== total) throw new Error(`staff read ${rows.length} of ${total} members`);
      return rows;
    }
  }
  throw new Error(`staff roster runs past ${STAFF_MAX_PAGES} pages`);
}

function hydrate(done?: () => void) {
  if (_loading) { done?.(); return; }
  _loading = true;
  const gen = _gen;
  const current = () => gen === _gen;
  /* A failed list read must be distinguishable from an empty roster (2026-07-31
     rule — see shell/composites LoadState). Recorded per call so a partial
     failure says which half is missing rather than blanking the screen. */
  _error = null;
  Promise.all([
    (typeof listStaff === "function" ? listAllStaff(current) : Promise.resolve([] as Record<string, any>[])).catch((e) => { if (current()) { console.warn("listStaff failed:", e); _error = "Couldn't load staff."; } return [] as Record<string, any>[]; }),
    (typeof listStaffGroups === "function" ? listStaffGroups() : Promise.resolve({ data: [], groups: [] })).catch((e) => { if (current()) { console.warn("listStaffGroups failed:", e); _error = _error ? "Couldn't load staff or groups." : "Couldn't load staff groups."; } return { data: [], groups: [] }; }),
  ]).then(([ms, gr]) => {
    /* A tenant switch purged this read mid-flight; the confirmed tenant hydrates its own. */
    if (!current()) { done?.(); return; }
    const gs = ((gr as any).data ?? (gr as any).groups ?? []) as any[];
    _cache = { members: ms.map(toMember), groups: gs.map(toGroup) };
    _loading = false;
    _notify();
    done?.();
  }).catch((e) => { if (!current()) { done?.(); return; } console.warn("staff hydrate failed:", e); _error = "Couldn't load staff."; _cache = { members: [], groups: [] }; _loading = false; _notify(); done?.(); });
}

export function useStaff() {
  const [, force] = useState(0);

  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _listeners.add(fn);
    // (CC, 2026-10-05) first-use read gated like the tenant-change one — roles without team.read would 403
    if (_cache === null && !_loading && can("team.read")) hydrate();
    return () => { _listeners.delete(fn); };
  }, []);

  const state = _cache ?? { members: [], groups: [] };
  return {
    members: state.members,
    groups: state.groups,
    loading: _loading || (_cache === null && can("team.read")), // (CC, 2026-10-05) not-allowed = empty, not loading forever
    /** Failed read — pass to <LoadState>; never render an empty roster over this. */
    error: _error,
    /** Create (no id yet) or update (existing id) a staff member via the real API. */
    upsertMember: async (m: StaffMember) => {
      const gen = _gen; // CC 0470: writes below land only in this workspace/session
      const members = _cache?.members ?? [];
      const isNew = !members.some((x) => x.id === m.id);
      /* `role` + `locations` were MISSING here (fixed 2026-08-06): the editor let
         you pick a Studio role and build a per-location schedule, the payload
         dropped both, and the response echoed back without them — so the screen
         silently reverted. Both are declared on CanonicalStaffMember and on the
         create/update payloads (api-contracts/src/staff.ts), so this is a
         send-what-the-contract-accepts fix, not a new field. */
      const input = { firstName: m.firstName, lastName: m.lastName, email: m.email, phone: m.phone, title: m.title, role: m.role ?? null, status: m.status, signsIn: m.signsIn, channels: m.channels, groups: m.groups, locations: m.locations, concurrency: m.concurrency, dailyCap: m.dailyCap, color: (m as any).color, serviceGrants: m.serviceGrants, resourceIds: m.resourceIds };
      /* `timezone` is deliberately NOT in this payload (re-applied 2026-09-08).
         No staff write schema accepts it, so sending it either 400s the write or
         is dropped silently — and the second is worse: the toast reports success
         over a field that reverts on the next read. It is read on the way IN
         (`toMember` maps timezone/time_zone) because the server does serve it;
         a write needs a contract field to land in, and there is not one yet. */
      /* No photo / photoUrl in this payload (CC 0465): the record's photo is the photo sub-resource's
         (setPhoto / removePhoto below). The server refuses a photoUrl it didn't set. */
      let saved: StaffMember = m;
      // Addendum item 7: await-before-commit — a rejected write must NOT be
      // "applied optimistically" behind a success toast (UIR-21 class).
      if (isNew && typeof createStaff === "function") saved = toMember(await createStaff(input as any));
      else if (!isNew && typeof updateStaff === "function") saved = toMember(await updateStaff(m.id, input as any));
      logUpsert("staff", isNew ? null : (members.find((x) => x.id === m.id) as unknown as Record<string, unknown>), saved as unknown as Record<string, unknown>, { name: [saved.firstName, saved.lastName].filter(Boolean).join(" ") || saved.email });
      /* Read the cache at WRITE time (CC 0476): a photo written during this await must survive. */
      if (isNew) { _setAt(gen, { members: [saved, ...(_cache?.members ?? [])] }); return saved; }
      return _mergeRecordAt(gen, m.id, saved);
    },
    /** Set or replace a stored member's photo (CC 0465): the picked file goes up as it is. Answers the versioned URL and the PUT's processing (what was stripped),
     *  and shows it on the member. Throws the server's refusal unchanged (the editor words it). */
    setPhoto: async (id: string, file: Blob): Promise<{ url: string; processing: { metadataRemoved?: string[]; processedAt?: string } | null }> => {
      const gen = _gen; // CC 0470: writes below land only in this workspace/session
      const res = await setStaffPhoto(id, file) as unknown as { data?: { url?: string; processing?: { metadataRemoved?: string[]; processedAt?: string } }; url?: string };
      const url = res?.data?.url ?? res?.url ?? "";
      _setAt(gen, { members: (_cache?.members ?? []).map((x) => (x.id === id ? { ...x, photo: url, photoUrl: url || null } : x)) });
      return { url, processing: res?.data?.processing ?? null };
    },
    /** Remove a member's photo (CC 0465). 404 STAFF_PHOTO_NOT_FOUND means it is already gone, which is success. */
    removePhoto: async (id: string): Promise<void> => {
      const gen = _gen; // CC 0470: writes below land only in this workspace/session
      try { await deleteStaffPhoto(id); }
      catch (e) { if ((e as { code?: unknown })?.code !== "STAFF_PHOTO_NOT_FOUND") throw e; }
      _setAt(gen, { members: (_cache?.members ?? []).map((x) => (x.id === id ? { ...x, photo: "", photoUrl: null } : x)) });
    },
    /**
     * DEACTIVATE a roster record. `deleteStaff` is a SOFT delete (2026-10-08,
     * Staff Library defect): the server keeps the record and its booking and
     * audit history and sets its status to inactive. Dropping the row from the
     * cache claimed an erasure the server never made, and the next read put it
     * straight back — so the cache now mirrors what the server holds. That
     * includes the working hours the delete cleared: the location assignments
     * stay, their schedules go (`withoutSchedules`).
     */
    removeMember: async (id: string) => {
      const gen = _gen; // CC 0470: writes below land only in this workspace/session
      if (typeof deleteStaff === "function") await deleteStaff(id);
      const prev = (_cache?.members ?? []).find((x) => x.id === id);
      if (!prev) return;
      const next = { ...prev, status: "inactive" as const, locations: withoutSchedules(prev.locations) };
      logUpsert("staff", prev as unknown as Record<string, unknown>, next as unknown as Record<string, unknown>, { name: [prev.firstName, prev.lastName].filter(Boolean).join(" ") || prev.email });
      _setAt(gen, { members: (_cache?.members ?? []).map((x) => (x.id === id ? next : x)) });
    },
    /**
     * Change ONLY a member's status, through the status route
     * (`updateStaffStatus`, PATCH /status) — never the whole-record update
     * (2026-10-08, Reactivate defect). `upsertMember` sends `locations`, and the
     * cached ones could still carry the schedules a deactivation cleared, so
     * reactivating wrote the old hours back into the working rule. The status
     * route takes `{ status }` alone, for every transition (active, on-leave,
     * inactive). Awaited before the cache moves: the cache holds the member the
     * server returns, and a rejected write leaves it untouched.
     */
    setMemberStatus: async (id: string, status: StaffMember["status"]) => {
      const gen = _gen; // CC 0470: writes below land only in this workspace/session
      if (typeof updateStaffStatus !== "function") throw new Error("updateStaffStatus is not available in this build.");
      const saved = toMember(await updateStaffStatus(id, status) as unknown as Record<string, any>);
      const prev = (_cache?.members ?? []).find((x) => x.id === id) ?? null;
      logUpsert("staff", prev as unknown as Record<string, unknown> | null, saved as unknown as Record<string, unknown>, { name: [saved.firstName, saved.lastName].filter(Boolean).join(" ") || saved.email });
      return (_cache?.members ?? []).some((x) => x.id === id) ? _mergeRecordAt(gen, id, saved) : saved;
    },
    duplicateMember: async (id: string) => {
      const gen = _gen; // CC 0470: writes below land only in this workspace/session
      try {
        if (typeof duplicateStaff === "function") {
          const copy = toMember(await duplicateStaff(id));
          _setAt(gen, { members: [copy, ...(_cache?.members ?? [])] });
          return copy;
        }
      } catch (e) { console.warn("duplicateStaff failed:", e); }
      return null;
    },
    /**
     * Replace where a staff member works and the role they hold at each
     * (2026-08-08, Staff & Roles — `setStaffLocationRoles`). The whole set goes
     * every time, so dropping a location is an ordinary edit. Awaited before
     * the cache moves: a rejected write must never leave the screen showing a
     * saved state (no-fake-interactivity).
     */
    setLocationRoles: async (staffId: string, assignments: StaffLocationAssignment[]) => {
      const gen = _gen; // CC 0470: writes below land only in this workspace/session
      if (typeof setStaffLocationRoles !== "function") throw new Error("setStaffLocationRoles is not available in this build.");
      const res = await setStaffLocationRoles(staffId, assignments) as unknown as Record<string, any>;
      const saved = toMember(res?.data ?? res?.staff ?? res);
      const prev = (_cache?.members ?? []).find((x) => x.id === staffId) ?? null;
      logUpsert("staff", prev as unknown as Record<string, unknown> | null, saved as unknown as Record<string, unknown>, { name: saved.name || saved.email });
      return (_cache?.members ?? []).some((x) => x.id === staffId) ? _mergeRecordAt(gen, staffId, saved) : saved;
    },
    /**
     * The person's DEFAULT role — what every location inherits unless it sets
     * its own (operator, 2026-08-08). Stored on the roster record's `role`,
     * which is where a person with no workspace membership can hold one too.
     * Awaited before the cache moves.
     */
    setDefaultRole: async (staffId: string, role: string | null) => {
      const gen = _gen; // CC 0470: writes below land only in this workspace/session
      if (typeof updateStaff !== "function") throw new Error("updateStaff is not available in this build.");
      const saved = toMember(await updateStaff(staffId, { role } as never) as unknown as Record<string, any>);
      return (_cache?.members ?? []).some((x) => x.id === staffId) ? _mergeRecordAt(gen, staffId, saved) : saved;
    },
    upsertGroup: async (g: StaffGroup) => {
      const gen = _gen; // CC 0470: writes below land only in this workspace/session
      const groups = _cache?.groups ?? [];
      const isNew = !groups.some((x) => x.id === g.id);
      const input = { name: g.name, description: g.description, hue: g.hue, status: g.status, services: g.services };
      let saved: StaffGroup = g;
      if (isNew && typeof createStaffGroup === "function") saved = toGroup(await createStaffGroup(input as any));
      else if (!isNew && typeof updateStaffGroup === "function") saved = toGroup(await updateStaffGroup(g.id, input as any));
      logUpsert("staffGroup", isNew ? null : (groups.find((x) => x.id === g.id) as unknown as Record<string, unknown>), saved as unknown as Record<string, unknown>, { name: saved.name });
      const next = isNew ? [saved, ...groups] : groups.map((x) => (x.id === g.id ? saved : x));
      _setAt(gen, { groups: next });
    },
    removeGroup: async (id: string) => {
      const gen = _gen; // CC 0470: writes below land only in this workspace/session
      if (typeof deleteStaffGroup === "function") await deleteStaffGroup(id);
      const prev = (_cache?.groups ?? []).find((x) => x.id === id);
      if (prev) logDelete("staffGroup", prev as unknown as Record<string, unknown>, { name: prev.name });
      _setAt(gen, { groups: (_cache?.groups ?? []).filter((x) => x.id !== id) });
    },
    duplicateGroup: async (id: string) => {
      const gen = _gen; // CC 0470: writes below land only in this workspace/session
      try {
        if (typeof duplicateStaffGroup === "function") {
          const copy = toGroup(await duplicateStaffGroup(id));
          _setAt(gen, { groups: [copy, ...(_cache?.groups ?? [])] });
          return copy;
        }
      } catch (e) { console.warn("duplicateStaffGroup failed:", e); }
      return null;
    },
    reload: () => { _cache = null; hydrate(); },
  };
}
